import timezones from "@/assets/timezones.json";
import { AddressInput } from "@/components/molecules/address-input";
import { Field } from "@/components/molecules/field";
import { FileUpload } from "@/components/molecules/file-upload";
import { FormFooter } from "@/components/molecules/form-footer";
import { SettingsSection } from "@/components/molecules/settings-section";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  useMyClients,
  useUpdateClient,
} from "@/features/clients/hooks/use-clients";
import { useHasRole } from "@/features/clients/hooks/use-has-role";
import {
  localizedCountries,
  localizedLanguages,
} from "@/features/utils/display-names";
import { useTranslation } from "@/features/utils/i18n";
import { formatTimezoneDisplay, groupTimezones } from "@/lib/timezone-utils";
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { WebsiteProposal } from "./website-proposal";

export function CompanySettingsGeneral() {
  const { t, i18n } = useTranslation("companySettings");
  const languages = useMemo(
    () => localizedLanguages(i18n.language),
    [i18n.language],
  );
  const countries = useMemo(
    () => localizedCountries(i18n.language),
    [i18n.language],
  );
  const { company: clientId } = useParams<{ company: string }>();
  const { data: clients = [] } = useMyClients();
  const updateClient = useUpdateClient();

  // Find current client
  const currentClient = useMemo(() => {
    return clients.find((c) => c.client.id === clientId);
  }, [clients, clientId]);

  const [name, setName] = useState(currentClient?.client.name || "");
  const [website, setWebsite] = useState(currentClient?.client.website || "");
  const [about, setAbout] = useState(currentClient?.client.about || "");
  const [address, setAddress] = useState(currentClient?.client.address || "");
  const [default_language, setDefaultLanguage] = useState(
    currentClient?.client.default_language || "",
  );
  const [default_country, setDefaultCountry] = useState(
    currentClient?.client.default_country || "",
  );
  const [default_timezone, setDefaultTimezone] = useState(
    currentClient?.client.default_timezone || "",
  );
  const [logoFile, setLogoFile] = useState<File | string | null>(
    currentClient?.client.logo || null,
  );

  // Back to what is saved — on load, after a save, and for « Annuler ».
  const reset = () => {
    if (!currentClient) return;
    setName(currentClient.client.name);
    setWebsite(currentClient.client.website || "");
    setAbout(currentClient.client.about || "");
    setAddress(currentClient.client.address || "");
    setDefaultLanguage(currentClient.client.default_language || "");
    setDefaultCountry(currentClient.client.default_country || "");
    setDefaultTimezone(currentClient.client.default_timezone || "");
    setLogoFile(currentClient.client.logo || null);
  };

  // Update local state when client data loads
  useMemo(reset, [currentClient]);

  const handleLogoChange = (file: File | File[] | string | null) => {
    // Handle single file only (FileUpload with multiple=false)
    if (Array.isArray(file)) {
      setLogoFile(file[0] || null);
    } else {
      setLogoFile(file);
    }
  };

  const handleSave = async () => {
    if (!clientId) return;

    try {
      const updates: {
        name?: string;
        logo?: string;
        website?: string;
        about?: string;
        address?: string;
        default_language?: string;
        default_country?: string;
        default_timezone?: string;
      } = {};

      // Only include changed fields
      if (name !== currentClient?.client.name) {
        updates.name = name;
      }
      // Compare against normalized values (null -> "")
      if (website !== (currentClient?.client.website || "")) {
        updates.website = website;
      }
      if (about !== (currentClient?.client.about || "")) {
        updates.about = about;
      }
      if (address !== (currentClient?.client.address || "")) {
        updates.address = address;
      }
      if (default_language !== (currentClient?.client.default_language || "")) {
        updates.default_language = default_language;
      }
      if (default_country !== (currentClient?.client.default_country || "")) {
        updates.default_country = default_country;
      }
      if (default_timezone !== (currentClient?.client.default_timezone || "")) {
        updates.default_timezone = default_timezone;
      }

      // Handle logo update
      if (logoFile instanceof File) {
        // Convert file to base64 using Promise
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(logoFile);
        });
        updates.logo = base64;
      } else if (logoFile === null && currentClient?.client.logo) {
        // Empty string to remove logo
        updates.logo = "";
      }

      if (Object.keys(updates).length > 0) {
        await updateClient.mutateAsync({
          clientId,
          data: updates,
        });

        toast.success(t("companySettings.messages.updated"));
      }
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t("companySettings.messages.updateError"),
      );
    }
  };

  // Group timezones for display
  const groupedTimezones = useMemo(
    () => groupTimezones(Object.keys(timezones).sort()),
    [],
  );

  const isOwner = useHasRole("OWNER");
  const hasChanges =
    name !== currentClient?.client.name ||
    website !== (currentClient?.client.website || "") ||
    about !== (currentClient?.client.about || "") ||
    address !== (currentClient?.client.address || "") ||
    default_language !== (currentClient?.client.default_language || "") ||
    default_country !== (currentClient?.client.default_country || "") ||
    default_timezone !== (currentClient?.client.default_timezone || "") ||
    logoFile instanceof File ||
    (logoFile === null && !!currentClient?.client.logo);

  if (!currentClient) {
    return <></>;
  }

  return (
    <form
      id="company-general-form"
      onSubmit={(e) => {
        e.preventDefault();
        void handleSave();
      }}
      className="contents"
    >
      <SettingsSection
        title={t("companySettings.sections.identity.title")}
        description={t("companySettings.sections.identity.description")}
      >
        {isOwner && (
          <WebsiteProposal
            website={website}
            current={{ name, about, address }}
            onApply={(values) => {
              if (values.name !== undefined) setName(values.name);
              if (values.about !== undefined) setAbout(values.about);
              if (values.address !== undefined) setAddress(values.address);
            }}
          />
        )}

        <Field label={t("companySettings.general.companyName")} htmlFor="name">
          <Input
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("companySettings.general.companyNamePlaceholder")}
            disabled={!isOwner}
            required
          />
        </Field>

        <Field
          label={t("companySettings.general.companyLogo")}
          hint={t("companySettings.general.logoRecommendation")}
        >
          {typeof logoFile === "string" && logoFile ? (
            <div className="flex items-center gap-4">
              <img
                src={logoFile}
                alt={t("companySettings.general.companyLogo")}
                className="h-24 w-24 rounded-lg object-cover border"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setLogoFile(null)}
                disabled={!isOwner}
              >
                {t("companySettings.general.removeLogo")}
              </Button>
            </div>
          ) : (
            <FileUpload
              type="image"
              accept="image/png, image/jpeg, image/jpg, image/gif"
              maxSize={5 * 1024 * 1024}
              value={logoFile instanceof File ? logoFile : null}
              onChange={handleLogoChange}
              disabled={!isOwner}
            />
          )}
        </Field>

        <Field label={t("companySettings.general.website")} htmlFor="website">
          <Input
            id="website"
            type="url"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            placeholder={t("companySettings.general.websitePlaceholder")}
            disabled={!isOwner}
          />
        </Field>

        <Field label={t("companySettings.general.about")} htmlFor="about">
          <Textarea
            id="about"
            value={about}
            onChange={(e) => setAbout(e.target.value)}
            placeholder={t("companySettings.general.aboutPlaceholder")}
            disabled={!isOwner}
            rows={4}
          />
        </Field>
      </SettingsSection>

      <SettingsSection
        title={t("companySettings.sections.locale.title")}
        description={t("companySettings.sections.locale.description")}
      >
        <Field
          label={t("companySettings.general.address")}
          htmlFor="address"
          hint={t("companySettings.general.addressVerificationHint")}
        >
          <AddressInput
            id="address"
            name="address"
            value={address}
            onChange={setAddress}
            onSelect={(result) => {
              console.log("Address verified:", result);
            }}
            placeholder={t("companySettings.general.addressPlaceholder")}
            disabled={!isOwner}
            autoVerify={true}
            required
          />
        </Field>

        <Field
          label={t("companySettings.general.defaultLanguage")}
          htmlFor="default_language"
          hint={t("companySettings.general.defaultLanguageHint")}
        >
          <Select
            value={default_language}
            onValueChange={setDefaultLanguage}
            disabled={!isOwner}
          >
            <SelectTrigger id="default_language">
              <SelectValue
                placeholder={t(
                  "companySettings.general.defaultLanguagePlaceholder",
                )}
              />
            </SelectTrigger>
            <SelectContent>
              {languages.map((lang) => (
                <SelectItem key={lang.code} value={lang.code}>
                  {lang.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Field
          label={t("companySettings.general.defaultCountry")}
          htmlFor="default_country"
          hint={t("companySettings.general.defaultCountryHint")}
        >
          <Select
            value={default_country}
            onValueChange={setDefaultCountry}
            disabled={!isOwner}
          >
            <SelectTrigger id="default_country">
              <SelectValue
                placeholder={t(
                  "companySettings.general.defaultCountryPlaceholder",
                )}
              />
            </SelectTrigger>
            <SelectContent>
              {countries.map((country) => (
                <SelectItem key={country.code} value={country.code}>
                  {country.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Field
          label={t("companySettings.general.defaultTimezone")}
          htmlFor="default_timezone"
          hint={t("companySettings.general.defaultTimezoneHint")}
        >
          <Select
            value={default_timezone}
            onValueChange={setDefaultTimezone}
            disabled={!isOwner}
          >
            <SelectTrigger id="default_timezone">
              <SelectValue
                placeholder={t(
                  "companySettings.general.defaultTimezonePlaceholder",
                )}
              />
            </SelectTrigger>
            <SelectContent className="max-h-[300px] overflow-y-auto">
              {groupedTimezones.map((group) => (
                <SelectGroup key={group.canonical}>
                  <SelectItem value={group.canonical}>
                    {formatTimezoneDisplay(group.canonical, group.offset)}
                    <span className="text-muted-foreground">
                      {group.alternatives.length > 0 && ", "}
                      {group.alternatives.slice(0, 3).join(", ")}
                      {group.alternatives.length > 3 &&
                        ` ${t("companySettings.general.moreTimezones", { count: group.alternatives.length - 3 })}`}
                    </span>
                  </SelectItem>
                </SelectGroup>
              ))}
            </SelectContent>
          </Select>
        </Field>

        {!isOwner && (
          <p className="text-sm text-muted-foreground">
            {t("companySettings.general.ownerRestriction")}
          </p>
        )}
      </SettingsSection>

      {isOwner && (
        <FormFooter
          dirty={hasChanges}
          saving={updateClient.isPending}
          onSave={handleSave}
          onCancel={reset}
          form="company-general-form"
        />
      )}
    </form>
  );
}
